if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // LH fork: no size selected by default — require an explicit choice before
        // Add to Cart is enabled, to cut down on wrong-size returns/chargebacks.
        // Self-contained and independent of Dawn 16's ProductSelectEvent pipeline
        // (that architecture only reacts to `change`, not initial page load) —
        // ported from production's own product-form.js, same approach, adapted to
        // work for both the Size dropdown and Size swatch-pill buttons since both
        // fire a native `change` event on their fieldset/wrapper. Matches Color's
        // fieldset already carrying an `option-{{ option.name }}` class (see
        // snippets/product-variant-picker.liquid).
        this.lhSizeOption = this.closest('.product')?.querySelector('.option-Size');
        if (this.lhSizeOption) {
          this.submitButton.setAttribute('disabled', true);
          this.submitButtonText.textContent = 'CHOOSE OPTION(S)';

          // LH: NOT persisted anywhere (no cookie/storage) and reset on every
          // fresh page load — this is deliberate. product-variant-picker.liquid
          // ALWAYS suppresses Size's checked/selected state server-side, no
          // matter what the URL says, because a size that's ever server-rendered
          // as pre-selected is a real returns/chargeback risk. So the ONLY way
          // Size may show as selected is a value this specific in-memory flag
          // says the customer clicked THIS page view — never inherited from a
          // `?variant=` link, browser history, or Dawn's own re-render.
          this.lhSizeChosen = false;
          this.lhChosenSizeValue = null;

          // LH: delegated on the stable `.product` ancestor, NOT on
          // this.lhSizeOption directly — Dawn 16's product-info.js
          // (updateOptionValues) fully replaces .option-Size's entire DOM
          // subtree on every AJAX variant-select swap, so a listener bound
          // straight to that node is left attached to a detached element
          // after the FIRST successful size change and silently never fires
          // again. That meant lhChosenSizeValue got stuck at whatever was
          // clicked first, and the variantChange subscriber below kept
          // faithfully (and wrongly) re-applying that stale value on every
          // later swap — reproduced live 2026-09-21: switching Twin ->
          // Full/Queen left Twin checked instead. `.product` itself is never
          // replaced, only descendants inside <variant-selects> are, so
          // delegating here survives every swap.
          const productRoot = this.closest('.product');
          productRoot?.addEventListener('change', (event) => {
            if (!event.target.closest('.option-Size')) return;
            const selectedValue = event.target.value;
            if (selectedValue) {
              this.lhSizeChosen = true;
              this.lhChosenSizeValue = selectedValue;
              this.submitButton.removeAttribute('disabled');
              this.submitButtonText.textContent = window.variantStrings?.addToCart || 'ADD TO CART';
            } else {
              this.lhSizeChosen = false;
              this.lhChosenSizeValue = null;
              this.submitButton.setAttribute('disabled', true);
              this.submitButtonText.textContent = 'CHOOSE OPTION(S)';
            }
          });

          // LH: Dawn 16's product-info.js (handleUpdateProductInfo ->
          // updateOptionValues) fully replaces the <variant-selects> DOM with a
          // fresh server render on every option change, and that render never
          // marks a Size value checked (see above) — so the customer's own click
          // would otherwise visually revert the instant the swap lands, even
          // though the correct variant did resolve. Re-apply it here, client-side
          // only, after the swap (variantChange publishes once updateOptionValues
          // has already run) — never by trusting anything the server sent back.
          //
          // Deliberately NOT filtering on data.sectionId here (it was tried and
          // removed 2026-09-21): product-info.js's own sectionId getter
          // (`dataset.originalSection || dataset.section`) returns the
          // UN-prefixed section id inside a quick-add modal, since that's what
          // Shopify's Section Rendering API can actually resolve — but
          // quick-add.js's preventDuplicatedIDs() rewrites this <product-form>'s
          // own data-section-id to a "quickadd-"-PREFIXED id. Comparing the two
          // always mismatched inside quick-add, so this subscriber silently
          // never fired there — the customer's first click only ever appeared
          // to work because the browser's native radio click checks the box for
          // free, before Dawn's very next AJAX swap discarded it with nothing
          // to correct it. Dropping the section check entirely is safe: the
          // .closest('.product') scoping below already isolates each
          // product-form instance, and re-applying an already-correct value in
          // response to an unrelated product's variant change is a harmless
          // no-op (idempotent).
          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            if (!this.lhSizeChosen) return;

            this.lhSizeOption = this.closest('.product')?.querySelector('.option-Size');
            if (!this.lhSizeOption) return;

            const select = this.lhSizeOption.querySelector('select');
            if (select) {
              const target = Array.from(select.options).find((option) => option.value === this.lhChosenSizeValue);
              if (!target) return;
              // LH: set the literal `selected` attribute too, not just the
              // `.selected`/`select.value` property — global.js's own
              // updateSelectionMetadata() locates the "currently selected"
              // option on the NEXT native change by searching for this
              // attribute (`.find((option) => option.getAttribute('selected'))`),
              // and throws if none is found. Property-only would work visually
              // but silently break the customer's next size change.
              select.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
              target.setAttribute('selected', 'selected');
              target.selected = true;
              return;
            }

            const radio = this.lhSizeOption.querySelector(
              `input[type="radio"][value="${CSS.escape(this.lhChosenSizeValue)}"]`
            );
            if (radio) radio.checked = true;
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
